/**
 * Field tickets on the technician's phone (PT-5, fieldiq-api#932).
 *
 *  - `PtFieldJobTickets` — the section on /field/pt/jobs/:id: the job's tickets
 *    (`GET /jobs/:id/tickets`) and, on an active per-day job, "Close out today".
 *  - `PtFieldTicketScreen` — /field/pt/tickets/:id: the ticket review (lines and
 *    quantities, "Add charge", edit a quantity, remove a line with a reason) and
 *    "Get company man signature", a full-screen summary the company man reads and
 *    signs on this phone.
 *
 * QUANTITIES ARE THE TECHNICIAN'S, PRICES ARE THE OFFICE'S. Nothing here sends a
 * rate, and the money column is the API's own verdict for each line — an
 * amount, "No charge", "No rate" or "Enter the quantity" — never a client-side
 * quantity × rate. The subtotal is the API's; NULL reads "No rate".
 *
 * SIGNING IS THE API'S CALL. The button is offered on a draft; a ticket with no
 * lines or an unpriced line is refused (`no_lines`, `unrated_lines`) and the
 * API's sentence is shown on the signing screen. A signed ticket is read-only —
 * the status the API sent, not a lock worked out here.
 *
 * PO and AFE start from the ticket (snapshotted from the job) and the company
 * man may change either on site (PT-6, fieldiq-api#939: the sign route takes
 * `po_number`). Each is sent only when it was changed — absent keeps the
 * ticket's own, a cleared field sends null.
 *
 * ONLINE ONLY, like the job signatures (PtFieldSignatures.tsx): nothing here is
 * queued for later, so in a dead zone the writes say they need signal instead
 * of pretending to save.
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { CalendarCheck, CheckCircle2, ChevronRight, Loader2, PenLine, Plus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import SignaturePad from '../../../components/SignaturePad';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import { useAuth } from '../../../contexts/AuthContext';
import { useClosePtDay, usePtJobTickets, usePtTicket, useSignPtTicket } from '../../../hooks/usePressureTesting';
import { formatCalendarDate, formatInstant } from '../../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import { useSyncStore } from '../../../lib/offline/sync-store';
import {
  canWritePtTests,
  ptLineAmountText,
  ptLineKindLabel,
  ptLineNeedsAttention,
  ptQtyText,
  ptSubtotalText,
  ptTicketStatusSkin,
} from '../../../lib/pressure-testing';
import type { PtJobDetail, PtTicketDetail, PtTicketLine } from '../../../types/pressure-testing';
import { PT_INPUT } from '../PtBits';
import { ptWriteError } from '../PtJobDialogs';
import { ptTicketScopeText } from '../FieldTicketDetailScreen';
import { PT_FIELD_CHARGE_KINDS, PtAddLineForm, PtEditLineForm, PtRemoveLineForm } from '../PtTicketLineForms';
import { ptTicketDate } from '../FieldTicketsScreen';
import {
  PT_FIELD_PRIMARY,
  PT_FIELD_SECONDARY,
  PtFieldBadge,
  PtFieldCard,
  PtFieldEmpty,
  PtFieldPage,
  PtFieldQueryState,
  PtFieldSectionTitle,
  PtRefusal,
} from './PtFieldBits';

export const ptFieldTicketPath = (ticketId: string, jobId?: string) =>
  `/field/pt/tickets/${ticketId}${jobId ? `?job=${encodeURIComponent(jobId)}` : ''}`;

function OfflineNote({ what }: { what: string }) {
  return (
    <p
      role="status"
      data-testid="pt-field-ticket-offline"
      className="rounded-fi-control border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card px-3 py-2 text-sm text-fieldiq-text"
    >
      Needs signal to {what}. It is not saved for later.
    </p>
  );
}

// ─── On the job screen ───────────────────────────────────────────────────────

export function PtFieldJobTickets({ job, today }: { job: PtJobDetail; today: string | null }) {
  const { user } = useAuth();
  const isOnline = useSyncStore((s) => s.isOnline);
  const tickets = usePtJobTickets(job.id);
  const closeDay = useClosePtDay();
  const [refusal, setRefusal] = useState<string | null>(null);
  const rows = tickets.data?.data ?? [];
  const day = tickets.data?.meta.today ?? today;
  const tz = tickets.data?.meta.time_zone ?? null;
  const canWrite = canWritePtTests(user, job);
  const failure = queryLoadFailure(tickets);

  if (job.status === 'cancelled') return null;

  const onCloseDay = () => {
    if (!day) return;
    setRefusal(null);
    closeDay.mutate(
      { jobId: job.id, workDate: day },
      {
        onSuccess: (res) => {
          toast.success(res.data?.ticket_number ? `Today is on ${res.data.ticket_number}` : 'Today is closed out');
        },
        onError: (err) => setRefusal(ptWriteError(err, 'Today was not closed out. Try again.').message),
      },
    );
  };

  return (
    <section className="mt-6" data-testid="pt-field-job-tickets">
      <PtFieldSectionTitle>Field tickets</PtFieldSectionTitle>
      {tickets.isLoading ? (
        <Loader2 className="size-5 animate-spin text-fieldiq-muted" aria-label="Loading this job's tickets" />
      ) : failure ? (
        <LoadFailureNotice error={queryFailureError(tickets)} kind={failure} subject="this job's tickets" />
      ) : rows.length === 0 ? (
        <PtFieldEmpty>
          {job.ticket_mode === 'per_day'
            ? 'No days are closed out yet. Close out each day you work.'
            : 'The field ticket is made when the job is completed.'}
        </PtFieldEmpty>
      ) : (
        <div className="space-y-2">
          {rows.map((t) => {
            const d = ptTicketDate(t, tz);
            const skin = ptTicketStatusSkin(t.status);
            return (
              <Link key={t.id} to={ptFieldTicketPath(t.id, job.id)} className="block" data-testid="pt-field-job-ticket">
                <PtFieldCard className={`flex min-h-[72px] items-center gap-3 border-l-4 px-4 py-3 active:bg-fieldiq-dark/50 ${skin.rail}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-base font-bold text-white">{t.ticket_number}</span>
                      <PtFieldBadge skin={skin} />
                    </div>
                    <p className="mt-1 text-sm text-fieldiq-text">
                      {d.sub} {d.text} · {ptSubtotalText(t)}
                    </p>
                  </div>
                  <ChevronRight className="size-6 shrink-0 text-fieldiq-muted" aria-hidden="true" />
                </PtFieldCard>
              </Link>
            );
          })}
        </div>
      )}

      {job.ticket_mode === 'per_day' && job.status === 'active' && canWrite && (
        <div className="mt-3 space-y-2">
          <PtRefusal message={refusal} testId="pt-field-close-day-refusal" />
          {!isOnline ? (
            <OfflineNote what="close out the day" />
          ) : (
            <button type="button" className={PT_FIELD_PRIMARY} onClick={onCloseDay} disabled={!day || closeDay.isPending} data-testid="pt-field-close-day">
              {closeDay.isPending ? <Loader2 className="size-5 animate-spin" aria-hidden="true" /> : <CalendarCheck className="size-5" aria-hidden="true" />}
              Close out today{day ? ` (${formatCalendarDate(day) ?? day})` : ''}
            </button>
          )}
        </div>
      )}
    </section>
  );
}

// ─── The ticket review ───────────────────────────────────────────────────────

function LineCard({
  line,
  currency,
  editable,
  onEdit,
  onRemove,
}: {
  line: PtTicketLine;
  currency: string;
  editable: boolean;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const warn = ptLineNeedsAttention(line);
  return (
    <PtFieldCard className={`border-l-4 px-4 py-3 ${warn ? 'border-l-fi-amber' : 'border-l-transparent'}`}>
      <div data-testid="pt-field-ticket-line" data-money={line.money}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-base font-bold text-white">{line.description}</p>
            <p className="mt-0.5 text-xs text-fieldiq-muted">{ptLineKindLabel(line.kind)}</p>
          </div>
          <p className={`shrink-0 text-right text-base tabular-nums ${warn ? 'font-bold text-fieldiq-text' : 'font-mono text-white'}`} data-testid="pt-field-ticket-line-amount">
            {ptLineAmountText(line, currency)}
          </p>
        </div>
        <div className="mt-2 flex items-center justify-between gap-3">
          {editable ? (
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark/40 px-3 font-mono text-lg font-bold tabular-nums text-white"
              aria-label={`Change quantity, ${line.description}`}
              data-testid="pt-field-ticket-line-qty"
            >
              {ptQtyText(line.quantity, line.unit)}
            </button>
          ) : (
            <span className="font-mono text-lg font-bold tabular-nums text-white">{ptQtyText(line.quantity, line.unit)}</span>
          )}
          {editable && (
            <button
              type="button"
              onClick={onRemove}
              className="inline-flex size-11 items-center justify-center rounded-fi-control text-fieldiq-muted"
              aria-label={`Remove ${line.description}`}
              data-testid="pt-field-ticket-line-remove"
            >
              <Trash2 className="size-5" aria-hidden="true" />
            </button>
          )}
        </div>
        {warn && line.unpriced_reason && <p className="mt-2 text-sm text-fieldiq-text">{line.unpriced_reason}</p>}
      </div>
    </PtFieldCard>
  );
}

/** The full-screen summary the company man reads and signs. */
function CompanyManSign({ ticket, onClose }: { ticket: PtTicketDetail; onClose: () => void }) {
  const sign = useSignPtTicket();
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [afe, setAfe] = useState(ticket.afe_number ?? '');
  const [po, setPo] = useState(ticket.po_number ?? '');
  const [costCode, setCostCode] = useState(ticket.cost_code ?? '');
  const [data, setData] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const submit = () => {
    if (!name.trim()) return setError("Enter the company man's name.");
    if (!data) return setError('Draw the signature and tap Confirm Signature.');
    setError(null);
    const body: Parameters<typeof sign.mutate>[0]['body'] = {
      signer_name: name.trim(),
      signer_title: title.trim() || null,
      cost_code: costCode.trim() || null,
      signature_data: data,
    };
    // AFE is sent only when it was changed: absent keeps the ticket's own.
    if (afe.trim() !== (ticket.afe_number ?? '')) body.afe_number = afe.trim() || null;
    // Same rule for the PO.
    if (po.trim() !== (ticket.po_number ?? '')) body.po_number = po.trim() || null;
    sign.mutate(
      { ticketId: ticket.id, body },
      {
        onSuccess: () => {
          toast.success(`${ticket.ticket_number} signed`);
          onClose();
        },
        onError: (err) => setError(ptWriteError(err, 'The ticket was not signed. Try again.').message),
      },
    );
  };

  const big = `${PT_INPUT} h-12 text-base`;
  return (
    <div role="dialog" aria-modal="true" aria-label={`Sign ${ticket.ticket_number}`} className="fixed inset-0 z-50 overflow-y-auto bg-fieldiq-dark" data-testid="pt-field-sign-screen">
      <div className="mx-auto max-w-3xl px-4 pb-10 pt-4">
        <div className="flex items-center justify-between gap-3">
          <p className="text-[0.7rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Field ticket for signature</p>
          <button type="button" onClick={onClose} aria-label="Close" className="inline-flex size-11 items-center justify-center rounded-fi-control text-fieldiq-text">
            <X className="size-6" aria-hidden="true" />
          </button>
        </div>
        <h2 className="font-mono text-3xl font-extrabold text-white">{ticket.ticket_number}</h2>
        <p className="mt-1 text-lg text-fieldiq-text">
          {ticket.customer_name || '—'} · {ticket.job_number || '—'}
        </p>
        <p className="text-base text-fieldiq-text">
          {[ticket.location_name, ticket.lsd].filter(Boolean).join(' · ') || '—'} · {ptTicketScopeText(ticket)}
        </p>

        <ul className="mt-5 divide-y divide-fieldiq-border rounded-fi-panel border border-fieldiq-border bg-fieldiq-card" data-testid="pt-field-sign-lines">
          {ticket.lines.map((l) => (
            <li key={l.id} className="flex items-start justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <p className="text-lg font-bold text-white">{l.description}</p>
                <p className="font-mono text-base text-fieldiq-text">{ptQtyText(l.quantity, l.unit)}</p>
              </div>
              <p className={`shrink-0 text-lg tabular-nums ${ptLineNeedsAttention(l) ? 'font-bold text-fieldiq-text' : 'font-mono text-white'}`}>
                {ptLineAmountText(l, ticket.currency)}
              </p>
            </li>
          ))}
          {ticket.lines.length === 0 && <li className="px-4 py-3 text-base text-fieldiq-text">No charges.</li>}
          <li className="flex items-center justify-between gap-3 px-4 py-4">
            <p className="text-sm font-black uppercase tracking-[0.12em] text-fieldiq-muted">Subtotal (before tax)</p>
            <p className="font-mono text-2xl font-extrabold tabular-nums text-white" data-testid="pt-field-sign-subtotal">
              {ptSubtotalText(ticket)}
            </p>
          </li>
        </ul>

        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Company man's name</span>
            <input className={big} value={name} onChange={(e) => setName(e.target.value)} maxLength={200} autoComplete="off" data-testid="pt-field-sign-name" />
          </label>
          <label className="block">
            <span className="mb-1 block text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Title</span>
            <input className={big} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={100} data-testid="pt-field-sign-title" />
          </label>
          <label className="block">
            <span className="mb-1 block text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">AFE</span>
            <input className={`${big} font-mono`} value={afe} onChange={(e) => setAfe(e.target.value)} maxLength={100} data-testid="pt-field-sign-afe" />
          </label>
          <label className="block">
            <span className="mb-1 block text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Cost code</span>
            <input className={`${big} font-mono`} value={costCode} onChange={(e) => setCostCode(e.target.value)} maxLength={100} data-testid="pt-field-sign-cost-code" />
          </label>
          <label className="block sm:col-span-2">
            <span className="mb-1 block text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">PO</span>
            <input className={`${big} font-mono`} value={po} onChange={(e) => setPo(e.target.value)} maxLength={100} data-testid="pt-field-sign-po" />
          </label>
        </div>

        <div className="mt-5">
          <SignaturePad label="Company man's signature" onSave={setData} onClear={() => setData(null)} />
        </div>
        <div className="mt-4 space-y-3">
          <PtRefusal message={error} testId="pt-field-sign-error" />
          <button type="button" className={PT_FIELD_PRIMARY} onClick={submit} disabled={sign.isPending} data-testid="pt-field-sign-submit">
            {sign.isPending ? <Loader2 className="size-5 animate-spin" aria-hidden="true" /> : <PenLine className="size-5" aria-hidden="true" />}
            Sign
          </button>
          <button type="button" className={PT_FIELD_SECONDARY} onClick={onClose} disabled={sign.isPending}>
            Back to the ticket
          </button>
        </div>
      </div>
    </div>
  );
}

type Panel = { kind: 'add' } | { kind: 'edit'; line: PtTicketLine } | { kind: 'remove'; line: PtTicketLine } | { kind: 'sign' };

export default function PtFieldTicketScreen() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isOnline = useSyncStore((s) => s.isOnline);
  const query = usePtTicket(id);
  const ticket = query.data?.data ?? null;
  const tz = query.data?.meta.time_zone ?? null;
  const today = query.data?.meta.today ?? null;
  const [panel, setPanel] = useState<Panel | null>(null);
  const notFound = (queryFailureError(query) as { response?: { status?: number } } | null)?.response?.status === 404;
  const jobId = ticket?.job_id ?? searchParams.get('job');
  const isDraft = ticket?.status === 'draft';
  const editable = isDraft && isOnline;

  return (
    <PtFieldPage title={ticket?.ticket_number || 'Field ticket'} backTo={jobId ? `/field/pt/jobs/${jobId}` : '/field/pt'} testId="pt-field-ticket">
      {notFound ? (
        <PtFieldEmpty>This field ticket was not found.</PtFieldEmpty>
      ) : (
        <PtFieldQueryState query={query} subject="this field ticket">
          {ticket && (
            <>
              <PtFieldCard className="px-4 py-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-lg font-extrabold text-fieldiq-text">{ticket.ticket_number}</span>
                  <PtFieldBadge skin={ptTicketStatusSkin(ticket.status)} testId="pt-field-ticket-status" />
                </div>
                <p className="mt-1 text-lg font-bold text-white">{ticket.customer_name || '—'}</p>
                <p className="mt-0.5 text-sm text-fieldiq-text">
                  {ticket.job_number || '—'} · {ptTicketScopeText(ticket)}
                </p>
              </PtFieldCard>

              {ticket.status === 'void' && (
                <p className="mt-3 rounded-fi-control border border-fieldiq-border border-l-4 border-l-fi-steel bg-fieldiq-card px-3 py-2 text-sm text-fieldiq-text">
                  This ticket is void. {ticket.void_reason || ''}
                </p>
              )}
              {ticket.signature && (
                <p
                  data-testid="pt-field-ticket-signed"
                  className="mt-3 rounded-fi-control border border-fieldiq-border border-l-4 border-l-fi-blue bg-fieldiq-card px-3 py-2 text-sm text-fieldiq-text"
                >
                  Signed by {ticket.signature.signer_name || '—'}
                  {ticket.signature.signer_title ? `, ${ticket.signature.signer_title}` : ''} — {formatInstant(ticket.signature.signed_at, tz) ?? '—'}. A signed
                  ticket cannot change; the office voids it and makes a new one to correct it.
                </p>
              )}

              <PtFieldSectionTitle>Charges</PtFieldSectionTitle>
              {ticket.lines.length === 0 ? (
                <PtFieldEmpty>No charges on this ticket yet.</PtFieldEmpty>
              ) : (
                <div className="space-y-2" data-testid="pt-field-ticket-lines">
                  {ticket.lines.map((l) =>
                    panel && (panel.kind === 'edit' || panel.kind === 'remove') && panel.line.id === l.id ? (
                      <PtFieldCard key={l.id} className="px-4 py-4">
                        {panel.kind === 'edit' ? (
                          <PtEditLineForm line={l} currency={ticket.currency} canPrice={false} big onDone={() => setPanel(null)} onCancel={() => setPanel(null)} />
                        ) : (
                          <PtRemoveLineForm line={l} big onDone={() => setPanel(null)} onCancel={() => setPanel(null)} />
                        )}
                      </PtFieldCard>
                    ) : (
                      <LineCard
                        key={l.id}
                        line={l}
                        currency={ticket.currency}
                        editable={!!editable}
                        onEdit={() => setPanel({ kind: 'edit', line: l })}
                        onRemove={() => setPanel({ kind: 'remove', line: l })}
                      />
                    ),
                  )}
                </div>
              )}

              <PtFieldCard className="mt-3 flex items-center justify-between gap-3 px-4 py-4">
                <span className="text-[0.7rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Subtotal (before tax)</span>
                <span className="font-mono text-xl font-extrabold tabular-nums text-white" data-testid="pt-field-ticket-subtotal">
                  {ptSubtotalText(ticket)}
                </span>
              </PtFieldCard>

              {isDraft && !isOnline && (
                <div className="mt-3">
                  <OfflineNote what="change or sign this ticket" />
                </div>
              )}

              {editable && panel?.kind !== 'add' && (
                <button type="button" className={`${PT_FIELD_SECONDARY} mt-3`} onClick={() => setPanel({ kind: 'add' })} data-testid="pt-field-add-charge">
                  <Plus className="size-5" aria-hidden="true" />
                  Add charge
                </button>
              )}
              {editable && panel?.kind === 'add' && (
                <PtFieldCard className="mt-3 px-4 py-4">
                  <h2 className="mb-3 text-base font-bold text-white">Add charge</h2>
                  <PtAddLineForm
                    ticket={ticket}
                    kinds={PT_FIELD_CHARGE_KINDS}
                    canPrice={false}
                    day={ticket.work_date ?? today}
                    big
                    onDone={() => setPanel(null)}
                    onCancel={() => setPanel(null)}
                  />
                </PtFieldCard>
              )}

              {editable && (
                <button type="button" className={`${PT_FIELD_PRIMARY} mt-6`} onClick={() => setPanel({ kind: 'sign' })} data-testid="pt-field-get-signature">
                  <PenLine className="size-5" aria-hidden="true" />
                  Get company man signature
                </button>
              )}
              {ticket.status === 'approved' && (
                <p className="mt-6 flex items-center gap-2 rounded-fi-control border border-fieldiq-border border-l-4 border-l-fi-green bg-fieldiq-card px-3 py-2 text-sm text-fieldiq-text">
                  <CheckCircle2 className="size-4 shrink-0" aria-hidden="true" />
                  Approved by the office.
                </p>
              )}
              {jobId && (
                <Link to={`/field/pt/jobs/${jobId}`} className={`${PT_FIELD_SECONDARY} mt-3`}>
                  Back to {ticket.job_number || 'the job'}
                </Link>
              )}

              {panel?.kind === 'sign' && <CompanyManSign ticket={ticket} onClose={() => setPanel(null)} />}
            </>
          )}
        </PtFieldQueryState>
      )}
    </PtFieldPage>
  );
}
